import { TEMPLATES, TemplateId } from '@/lib/signature';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface TemplatePickerProps {
  value: TemplateId;
  onChange: (id: TemplateId) => void;
}

export function TemplatePicker({ value, onChange }: TemplatePickerProps) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {TEMPLATES.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={cn(
              'group relative rounded-xl border p-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'border-primary bg-primary/5 shadow-sm'
                : 'border-border hover:border-primary/50 hover:bg-muted/50',
            )}
          >
            {active && (
              <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </span>
            )}
            <TemplateThumbnail id={t.id} active={active} />
            <div className="mt-2 text-xs font-semibold">{t.name}</div>
            <div className="text-[10px] leading-tight text-muted-foreground">
              {t.description}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TemplateThumbnail({ id, active }: { id: TemplateId; active: boolean }) {
  const bar = active ? 'bg-primary' : 'bg-muted-foreground/40';
  const line = 'bg-muted-foreground/25';
  return (
    <div className="flex h-14 w-full items-center gap-2 rounded-md bg-background/60 p-2 ring-1 ring-border/60">
      {id === 'modern' && (
        <>
          <div className="h-8 w-8 shrink-0 rounded bg-muted-foreground/20" />
          <div className={cn('h-full w-0.5 rounded', bar)} />
          <div className="flex-1 space-y-1">
            <div className={cn('h-1.5 w-3/4 rounded', bar)} />
            <div className={cn('h-1 w-full rounded', line)} />
            <div className={cn('h-1 w-2/3 rounded', line)} />
          </div>
        </>
      )}
      {id === 'classic' && (
        <div className="flex-1 space-y-1">
          <div className={cn('h-1.5 w-2/3 rounded', bar)} />
          <div className={cn('h-1 w-1/2 rounded', line)} />
          <div className={cn('h-1 w-full rounded', line)} />
          <div className={cn('h-1 w-3/4 rounded', line)} />
        </div>
      )}
      {id === 'compact' && (
        <div className="flex-1">
          <div className={cn('h-1.5 w-full rounded', bar)} />
          <div className={cn('mt-1.5 h-1 w-4/5 rounded', line)} />
        </div>
      )}
      {id === 'card' && (
        <div className="flex h-full w-full items-center gap-2 rounded border border-dashed border-muted-foreground/30 p-1.5">
          <div className="flex-1 space-y-1">
            <div className={cn('h-1.5 w-3/4 rounded', bar)} />
            <div className={cn('h-1 w-full rounded', line)} />
          </div>
          <div className="h-8 w-8 shrink-0 rounded bg-muted-foreground/20" />
        </div>
      )}
      {id === 'banner' && (
        <div className="flex h-full w-full flex-col overflow-hidden rounded">
          <div className={cn('h-4 w-full rounded-t', bar)} />
          <div className="flex-1 space-y-1 bg-background/60 p-1.5">
            <div className={cn('h-1 w-full rounded', line)} />
            <div className={cn('h-1 w-2/3 rounded', line)} />
          </div>
        </div>
      )}
      {id === 'elegant' && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1">
          <div className={cn('h-1.5 w-1/2 rounded', bar)} />
          <div className={cn('h-0.5 w-6 rounded', bar)} />
          <div className={cn('h-1 w-2/3 rounded', line)} />
          <div className={cn('h-1 w-1/2 rounded', line)} />
        </div>
      )}
      {id === 'stacked' && (
        <div className="flex h-full w-full overflow-hidden rounded">
          <div className={cn('w-1 shrink-0', bar)} />
          <div className="w-1/3 shrink-0 space-y-1 bg-muted-foreground/10 p-1.5">
            <div className="h-4 w-4 rounded bg-muted-foreground/25" />
            <div className={cn('h-1 w-full rounded', bar)} />
          </div>
          <div className="flex-1 space-y-1 p-1.5">
            <div className={cn('h-1 w-full rounded', line)} />
            <div className={cn('h-1 w-3/4 rounded', line)} />
          </div>
        </div>
      )}
      {id === 'minimal' && (
        <div className="flex-1 space-y-1.5">
          <div className={cn('h-1.5 w-1/2 rounded', bar)} />
          <div className={cn('h-1 w-2/3 rounded', line)} />
          <div className={cn('h-1 w-1/2 rounded', line)} />
        </div>
      )}
    </div>
  );
}
